import { TFeedFilterByQueryStringNames } from "#next/components/explorer/base/explorerQueryStrings";
import { SearchResult } from "#next/utils/interfaces";

export interface FrequencyObject {
  [key: string]: number;
}

export interface RankingData {
  name: string;
  count: number;
  platforms: string[];
}

const addToCount = (
  counts: Record<string, number>,
  key: string,
  value: number,
) => {
  const currentCount = counts[key];
  counts[key] = currentCount === undefined ? value : currentCount + value;
};

const addToPlatforms = (
  platform: string,
  platforms: Record<string, Set<string>>,
  value: string,
) => {
  const set = platforms[value];
  if (set === undefined) {
    platforms[value] = new Set([platform]);
  } else {
    set.add(platform);
  }
};

const excludeUsername = (searchResult: SearchResult) => {
  const lowerCasedUsername = searchResult.username?.toLowerCase();
  if (!lowerCasedUsername) {
    return true;
  }
  switch (searchResult.platform) {
    case "Reddit":
      return lowerCasedUsername === "automoderator";
    default:
      return (
        lowerCasedUsername === "anonymous" || lowerCasedUsername === "null"
      );
  }
};

export function generateRankings(
  searchResults: SearchResult[],
  field: Exclude<TFeedFilterByQueryStringNames, "language">,
  limit?: number,
): RankingData[] {
  const counts: Record<string, number> = {};
  const platforms: Record<string, Set<string>> = {};
  const searchResultField = filterNameToSearchResultField[field];
  for (const searchResult of searchResults) {
    const valueOrValues = searchResult[searchResultField];
    if (valueOrValues === null) {
      continue;
    }
    if (field === "username" && excludeUsername(searchResult)) {
      continue;
    }
    if (Array.isArray(valueOrValues)) {
      for (const value of valueOrValues) {
        addToCount(counts, value, 1);
        addToPlatforms(searchResult.platform, platforms, value);
      }
    } else {
      addToCount(counts, valueOrValues, 1);
      const set = platforms[valueOrValues];
      if (set === undefined) {
        platforms[valueOrValues] = new Set([searchResult.platform]);
      } else {
        set.add(searchResult.platform);
      }
    }
  }
  let entries = Object.entries(counts).sort((a, b) => (a[1] < b[1] ? 1 : -1));
  if (field !== "platform") {
    entries = entries.splice(0, 20);
  }
  const entriesFormatted = entries.map(([name, count]) => {
    return {
      name,
      count,
      platforms: Array.from(platforms[name] ?? []).sort(),
    };
  });
  if (limit) return entriesFormatted.slice(0, limit);
  else return entriesFormatted;
}

type SearchResultCountFields = keyof Pick<
  SearchResult,
  "hashtags" | "links" | "platform" | "username" | "entities"
>;

const filterNameToSearchResultField: Readonly<
  Record<
    Exclude<TFeedFilterByQueryStringNames, "language">,
    SearchResultCountFields
  >
> = {
  hashtag: "hashtags",
  link: "links",
  platform: "platform",
  username: "username",
  entity: "entities",
} as const;
